import React, { useState, useEffect, useCallback } from "react";
import { Typography, Button, TextField, Grid, FormGroup } from "@mui/material";
import useStyles from "./style";
import { useFormik } from "formik";
import * as yup from "yup";
import { showLoader, hideLoader } from "Redux/Loader/actions";
import { showSnackbar } from "Redux/SnackBar/actions";
import { useDispatch } from "react-redux";
import _ from "lodash";
import MenuItem from "@mui/material/MenuItem";

//Api routes
import settingsApi from "Services/Settings";

//
import currencyData from "./data/currencyList.json";
//

import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormControl from "@mui/material/FormControl";
import FormLabel from "@mui/material/FormLabel";

import CustomSwitch from "SharedComponents/CustomSwitch";

const Settings = () => {
  const classes = useStyles();
  const dispatch = useDispatch();

  // States
  const [values, setValues] = useState([]);

  // Toggle
  const [cashPayments, setCashPayments] = useState(false);
  const [stripeCardPayments, setStripeCardPayments] = useState(false);

  //
  const currencies = Object.keys(currencyData);

  const handleSettings = (formValues) => {
    dispatch(showLoader("Loading please wait..."));

    let hotelCommissionAmount = 0;
    let hotelCommissionPercentage = 0;

    if (formValues?.hotelCommissionMode === "amount") {
      hotelCommissionAmount = formValues?.hotelCommissionValue;
    } else {
      hotelCommissionPercentage = formValues?.hotelCommissionValue;
    }

    const data = {
      taxPercentage: formValues?.taxPercentage,
      commissionStat: formValues?.commissionEnable === "ENABLE" ? true : false,
      commissionPercentage: formValues?.commissionPercentage,
      hotelMinimumWalletAmount: formValues?.hotelMinimumWalletAmount,
      bookingIdPrefix: formValues?.bookingIdPrefix,
      isTripFareEditable: formValues?.tripFare === "ENABLE" ? true : false,
      hotelCommissionType:
        formValues?.hotelCommissionMode === "amount" ? "AMOUNT" : "PERCENTAGE",
      hotelCommissionAmount: hotelCommissionAmount,
      hotelCommissionPercentage: hotelCommissionPercentage,
      currency: formValues?.currencyCode,
      cashPayments: cashPayments,
      stripeCardPayments: stripeCardPayments,
    };
    const onSuccess = (res) => {
      dispatch(hideLoader());
      if (res?.data?.status === true) {
        dispatch(
          showSnackbar({
            message: res?.data?.message || "Updated payment settings successfully",
            autoHideDuration: 3000,
            anchorOrigin: {
              vertical: "top",
              horizontal: "right",
            },
            variant: "success",
          })
        );
        formik?.resetForm();
        setValues(null);
        getMasterValues();
      }
    };
    const onFailure = (err) => {
      dispatch(hideLoader());
      dispatch(
        showSnackbar({
          message: err?.response?.data?.message || "Error while update settings",
          autoHideDuration: 3000,
          anchorOrigin: {
            vertical: "top",
            horizontal: "right",
          },
          variant: "error",
        })
      );
      //console.log("settings value update api", err);
    };
    settingsApi.updatePaymentSettings({ ...data }).then(onSuccess, onFailure);
  };

  const formik = useFormik({
    initialValues: {
      taxPercentage: "0",
      commissionEnable: "ENABLE",
      commissionPercentage: "0",
      hotelMinimumWalletAmount: "0",
      bookingIdPrefix: "SE",
      tripFare: "ENABLE",
      hotelCommissionMode: "amount",
      hotelCommissionValue: "0",
      currencyCode: "USD",
    },
    enableReinitialize: true,
    validationSchema: yup.object({
      taxPercentage: yup
        .number()
        .typeError("Must be a number")
        .min(0, "Must be greater than or equal to 0")
        .nullable(),
      bookingIdPrefix: yup.string().required("This field is required"),
      commissionPercentage: yup
        .number()
        .typeError("Must be a number")
        .min(0, "Must be greater than or equal to 0")
        .max(100, "Must be letss than or equal to 100"),
      hotelMinimumWalletAmount: yup
        .number()
        .typeError("Must be a number")
        .min(0, "Must be greater than or equal to 0")
        .max(100, "Must be letss than or equal to 100"),
      hotelCommissionValue: yup
        .number()
        .typeError("Must be a number")
        .min(0, "Must be greater than or equal to 0")
        .max(500, "Must be less than or equal to 500"),
    }),
    onSubmit: handleSettings,
  });

  // Get Payment Settings
  const getMasterValues = useCallback(() => {
    dispatch(showLoader("Loading please wait..."));
    const onSuccess = (res) => {
      //console.log("payment settings details ", res.data);

      dispatch(hideLoader());
      if (res?.data?.status === true) {
        setValues(res?.data?.data);
      } else {
        setValues([]);
      }
    };
    const onFailure = (err) => {
      dispatch(hideLoader());
      dispatch(
        showSnackbar({
          message: err?.response?.data?.message || "Failed to fetch data",
          autoHideDuration: 3000,
          anchorOrigin: {
            vertical: "top",
            horizontal: "right",
          },
          variant: "error",
        })
      );
    };
    settingsApi.getPaymentSettings().then(onSuccess, onFailure);
  }, []);

  useEffect(() => {
    getMasterValues();
  }, [getMasterValues]);

  // Formik - Set fetched values for master
  useEffect(() => {
    if (!_.isEmpty(values)) {
      formik.setFieldValue("taxPercentage", values?.taxPercentage);

      if (values?.commissionStat === true) {
        formik.setFieldValue("commissionEnable", "ENABLE");
      } else {
        formik.setFieldValue("commissionEnable", "DISABLE");
      }

      formik.setFieldValue(
        "commissionPercentage",
        values?.commissionPercentage
      );

      formik.setFieldValue(
        "hotelMinimumWalletAmount",
        values?.hotelMinimumWalletAmount
      );

      formik.setFieldValue("bookingIdPrefix", values?.bookingIdPrefix);

      if (values?.isTripFareEditable === true) {
        formik.setFieldValue("tripFare", "ENABLE");
      } else {
        formik.setFieldValue("tripFare", "DISABLE");
      }

      if (values?.hotelCommissionType === "AMOUNT") {
        formik.setFieldValue("hotelCommissionMode", "amount");
      } else {
        formik.setFieldValue("hotelCommissionMode", "percentage");
      }

      if (values?.hotelCommissionType === "AMOUNT") {
        formik.setFieldValue(
          "hotelCommissionValue",
          values?.hotelCommissionAmount
        );
      } else {
        formik.setFieldValue(
          "hotelCommissionValue",
          values?.hotelCommissionPercentage
        );
      }

      formik.setFieldValue("currencyCode", values?.currency);

      if (values?.cashPayments) {
        setCashPayments(values?.cashPayments);
      }

      if (values?.stripeCardPayments) {
        setStripeCardPayments(values?.stripeCardPayments);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values]);

  const handleCashPayments = (type) => {
    if (cashPayments === true) {
      setCashPayments(false);
    } else {
      setCashPayments(true);
    }
  };

  const handleStripeCardPayments = (type) => {
    if (stripeCardPayments === true) {
      setStripeCardPayments(false);
    } else {
      setStripeCardPayments(true);
    }
  };

  return (
    <>
      <br></br>
      <br></br>
      <Grid container className={classes.cardContainer}>
        <Grid
          item
          xs={12}
          md={12}
          lg={12}
          xl={12}
          className={classes.container}
        >
          <div style={{ padding: "15px" }}>
            <Typography variant="h5">Payment Modes</Typography>
          </div>
        </Grid>

        <Grid
          container
          xs={12}
          md={12}
          lg={12}
          xl={12}
          className={classes.paymentModeContainer}
        >
          <Grid
            item
            xs={12}
            md={6}
            lg={6}
            xl={6}
            className={classes.fieldWrapper}
          >
            <FormGroup>
              <FormControlLabel
                control={
                  <CustomSwitch
                    checked={cashPayments}
                    onChange={() => handleCashPayments()}
                  />
                }
                label="Cash Payments"
              />
            </FormGroup>
          </Grid>

          <Grid
            item
            xs={12}
            md={6}
            lg={6}
            xl={6}
            className={classes.fieldWrapper}
          >
            <FormGroup>
              <FormControlLabel
                control={
                  <CustomSwitch
                    checked={stripeCardPayments}
                    onChange={() => handleStripeCardPayments()}
                  />
                }
                label="Stripe (Card Payments)"
              />
            </FormGroup>
          </Grid>
        </Grid>

        {/* <Grid item xs={12} md={6} lg={12} xl={12} className={classes.btn}>
          <Button variant="contained" onClick={formik.handleSubmit}>
            Update
          </Button>
        </Grid> */}
      </Grid>

      <Grid container className={classes.cardContainer}>
        <Grid
          item
          xs={12}
          md={12}
          lg={12}
          xl={12}
          className={classes.container}
        >
          <div style={{ padding: "15px" }}>
            <Typography variant="h5">Trip Settings</Typography>
          </div>
        </Grid>

        <Grid item xs={12} md={6} lg={6} xl={6} className={classes.container}>
          {/* Tax Percentage */}
          <Grid
            item
            xs={12}
            md={6}
            lg={12}
            xl={12}
            className={classes.fieldWrapper}
          >
            <TextField
              name="taxPercentage"
              id="taxPercentage"
              value={formik?.values?.taxPercentage}
              defaultValue={formik?.values?.taxPercentage}
              onChange={formik?.handleChange}
              type={"text"}
              fullWidth
              variant="outlined"
              size="small"
              label="Tax Percentage(%)"
              error={
                formik?.errors?.taxPercentage && formik?.touched?.taxPercentage
              }
              helperText={
                formik?.errors?.taxPercentage &&
                formik?.touched?.taxPercentage &&
                formik?.errors?.taxPercentage
              }
            />
          </Grid>
          {/* Commission Enable/Disable */}
          <Grid
            item
            xs={12}
            md={6}
            lg={12}
            xl={12}
            className={classes.fieldWrapper}
          >
            <TextField
              name="commissionEnable"
              id="commissionEnable"
              select
              label="Commission Enable"
              value={formik?.values?.commissionEnable}
              defaultValue={formik?.values?.commissionEnable}
              onChange={formik?.handleChange}
              error={
                formik?.errors?.commissionEnable &&
                formik?.touched?.commissionEnable
              }
              helperText={
                formik?.errors?.commissionEnable &&
                formik?.touched?.commissionEnable &&
                formik?.errors?.commissionEnable
              }
              fullWidth
              variant="outlined"
              size="small"
            >
              {["ENABLE", "DISABLE"].map((option) => (
                <MenuItem key={option} value={option}>
                  {option === "ENABLE" ? "Enable" : "Disable"}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          {/* Commission Percentage */}
          <Grid
            item
            xs={12}
            md={6}
            lg={12}
            xl={12}
            className={classes.fieldWrapper}
          >
            <TextField
              name="commissionPercentage"
              id="commissionPercentage"
              value={formik?.values?.commissionPercentage}
              onChange={formik?.handleChange}
              type={"text"}
              fullWidth
              variant="outlined"
              size="small"
              label="Commission Percentage(%)"
              error={
                formik?.errors?.commissionPercentage &&
                formik?.touched?.commissionPercentage
              }
              helperText={
                formik?.errors?.commissionPercentage &&
                formik?.touched?.commissionPercentage &&
                formik?.errors?.commissionPercentage
              }
            />
          </Grid>
          {/* Hotel Minimum Valet Amount */}
          <Grid
            item
            xs={12}
            md={6}
            lg={12}
            xl={12}
            className={classes.fieldWrapper}
          >
            <TextField
              name="hotelMinimumWalletAmount"
              id="hotelMinimumWalletAmount"
              value={formik?.values?.hotelMinimumWalletAmount}
              onChange={formik?.handleChange}
              type={"text"}
              fullWidth
              variant="outlined"
              size="small"
              label="Hotel Minimum Wallet Amount"
              error={
                formik?.errors?.hotelMinimumWalletAmount &&
                formik?.touched?.hotelMinimumWalletAmount
              }
              helperText={
                formik?.errors?.hotelMinimumWalletAmount &&
                formik?.touched?.hotelMinimumWalletAmount &&
                formik?.errors?.hotelMinimumWalletAmount
              }
            />
          </Grid>
          {/* Booking Id Prefix */}
          <Grid
            item
            xs={12}
            md={6}
            lg={12}
            xl={12}
            className={classes.fieldWrapper}
          >
            <TextField
              name="bookingIdPrefix"
              id="bookingIdPrefix"
              value={formik?.values?.bookingIdPrefix}
              onChange={formik?.handleChange}
              type={"text"}
              fullWidth
              variant="outlined"
              size="small"
              label="Booking Id Prefix"
              error={
                formik?.errors?.bookingIdPrefix &&
                formik?.touched?.bookingIdPrefix
              }
              helperText={
                formik?.errors?.bookingIdPrefix &&
                formik?.touched?.bookingIdPrefix &&
                formik?.errors?.bookingIdPrefix
              }
            />
          </Grid>
          {/* Trip Fare */}
          <Grid
            item
            xs={12}
            md={6}
            lg={12}
            xl={12}
            className={classes.fieldWrapper}
          >
            <TextField
              name="tripFare"
              id="tripFare"
              select
              label="Trip Fare"
              value={formik?.values?.tripFare}
              defaultValue={formik?.values?.tripFare}
              onChange={formik?.handleChange}
              error={formik?.errors?.tripFare && formik?.touched?.tripFare}
              helperText={
                formik?.errors?.tripFare &&
                formik?.touched?.tripFare &&
                formik?.errors?.tripFare
              }
              fullWidth
              variant="outlined"
              size="small"
            >
              {["ENABLE", "DISABLE"].map((option) => (
                <MenuItem key={option} value={option}>
                  {option === "ENABLE" ? "Enable" : "Disable"}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>

        <Grid item xs={12} md={6} lg={6} xl={6} className={classes.container}>
          {/* Hotel Commission */}
          <Grid
            item
            xs={6}
            md={6}
            lg={6}
            xl={6}
            className={classes.fieldWrapper}
          >
            <div>
              <FormControl>
                <FormLabel id="demo-radio-buttons-group-label">
                  Hotel Commission
                </FormLabel>
                <RadioGroup
                  aria-labelledby="demo-radio-buttons-group-label"
                  defaultValue={formik?.values?.hotelCommissionMode}
                  value={formik?.values?.hotelCommissionMode}
                  onChange={formik?.handleChange}
                  name="hotelCommissionMode"
                  id="hotelCommissionMode"
                  row
                >
                  <FormControlLabel
                    value="amount"
                    control={<Radio />}
                    label="Amount"
                  />
                  <FormControlLabel
                    value="percentage"
                    control={<Radio />}
                    label="Percentage"
                  />
                </RadioGroup>
              </FormControl>
            </div>
          </Grid>

          <Grid
            item
            xs={12}
            md={12}
            lg={12}
            xl={12}
            className={classes.fieldWrapper}
          >
            <TextField
              name="hotelCommissionValue"
              id="hotelCommissionValue"
              value={formik?.values?.hotelCommissionValue}
              onChange={formik?.handleChange}
              type={"text"}
              fullWidth
              variant="outlined"
              size="small"
              label="Value"
              error={
                formik?.errors?.hotelCommissionValue &&
                formik?.touched?.hotelCommissionValue
              }
              helperText={
                formik?.errors?.hotelCommissionValue &&
                formik?.touched?.hotelCommissionValue &&
                formik?.errors?.hotelCommissionValue
              }
            />
          </Grid>

          {/* Currency */}
          <Grid
            item
            xs={12}
            md={6}
            lg={12}
            xl={12}
            className={classes.fieldWrapper}
          >
            <TextField
              name="currencyCode"
              id="currencyCode"
              select
              label="Currency"
              value={formik?.values?.currencyCode}
              defaultValue={formik?.values?.currencyCode}
              onChange={formik?.handleChange}
              error={
                formik?.errors?.currencyCode && formik?.touched?.currencyCode
              }
              helperText={
                formik?.errors?.currencyCode &&
                formik?.touched?.currencyCode &&
                formik?.errors?.currencyCode
              }
              fullWidth
              variant="outlined"
              size="small"
            >
              {currencies.map((currencyCode) => (
                <MenuItem key={currencyCode} value={currencyCode}>
                  {currencyData[currencyCode].name} ({currencyCode})
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>

        <Grid
          item
          xs={12}
          md={6}
          lg={6}
          xl={6}
          className={classes.container}
        ></Grid>
        <Grid item xs={12} md={6} lg={12} xl={12} className={classes.btn}>
          <Button variant="contained" onClick={formik.handleSubmit}>
            Update
          </Button>
        </Grid>
      </Grid>
      <br></br>
      <br></br>
      <br></br>
    </>
  );
};

export default Settings;
